import { TableHeadCell, Table, TableRow, IconButton, TableCell, Pagination } from '../../Common';
import { Evaluation, EvaluationStatus } from '../../../commonTypes/evaluation/calculate.types';
import { useNavigate } from 'react-router';
import { useMemo } from 'react';
import { displayRoles } from '../../../util/evaluation/mappers';
import { useSelector } from 'react-redux';
import { selectUserSub } from '../../../store/slices/auth.slice';
import { RootState } from '../../../store';
import { EvaluationSortBy, UserDto } from '../../../commonTypes/user.types';
import { formatToFullMonthDayYear } from '../../../util/date/dateFormater';
import { useTranslation } from '../../../hooks/useTranslation';
import StatusBadge from '../../StatusBadge';
import NoResultsFound from '../../Common/NoResultsFound';
import { checkIfAdminOrOwnerOrHr } from '../../../api/utils/roleUtils';

export default function EvaluationTable({
    activeTab,
    tableInfo,
    buttonType,
    buttonType2,
    setIsModalVisible,
    setEvaluationId,
    handleApproveOrReject,
    isReady,
    pagination,
    searchTerm,
    sortBy,
    sortDirection,
    setSortBy,
    setSortDirection,
}: {
    activeTab: number;
    tableInfo: Evaluation[];
    buttonType?: string;
    buttonType2?: string;
    setIsModalVisible?: React.Dispatch<React.SetStateAction<boolean>>;
    setEvaluationId?: React.Dispatch<React.SetStateAction<string>>;
    handleApproveOrReject?: (id: string, status: EvaluationStatus.UPDATE_APPROVED | EvaluationStatus.SUBMITTED) => void;
    isReady?: boolean;
    pagination: {
        total: number;
        perPage: number;
        setPerPage: React.Dispatch<React.SetStateAction<number>>;
        page: number;
        handlePageChange: React.Dispatch<React.SetStateAction<number>>;
    };
    searchTerm: string;
    setSortBy: React.Dispatch<React.SetStateAction<EvaluationSortBy>>;
    setSortDirection: React.Dispatch<React.SetStateAction<'DESC' | 'ASC'>>;
    sortBy: EvaluationSortBy;
    sortDirection: 'DESC' | 'ASC';
}) {
    const navigate = useNavigate();
    const { t } = useTranslation('evaluations');
    const userSub = useSelector(selectUserSub);
    const user = useSelector<RootState, UserDto>(state => state.auth.value);
    const isOwnerAdminHr = checkIfAdminOrOwnerOrHr(user.role.name);
    const todayAtMidnight = new Date();
    todayAtMidnight.setHours(0, 0, 0, 0);
    const isExpired = (dueDate: Date) => todayAtMidnight > new Date(dueDate);
    const isEvaluator = (user: string | undefined) => {
        if (!user) return false;
        return user === userSub;
    };

    const filteredTableInfo = useMemo(() => {
        const searchLower = searchTerm.toLowerCase();

        const matchesName = (profile?: { givenName?: string; familyName?: string }) => {
            if (!profile) return false;

            const given = profile.givenName?.toLowerCase() ?? '';
            const family = profile.familyName?.toLowerCase() ?? '';

            const fullName1 = `${given} ${family}`;
            const fullName2 = `${family} ${given}`;

            return fullName1.includes(searchLower) || fullName2.includes(searchLower);
        };

        let result = !searchTerm
            ? [...tableInfo]
            : tableInfo.filter(info => matchesName(info.evaluator?.profile) || matchesName(info.evaluatee?.profile));

        if (sortBy === EvaluationSortBy.EVALUATOR) {
            result.sort((a, b) => {
                const aIsAnon = !a.evaluator?.profile;
                const bIsAnon = !b.evaluator?.profile;

                if (aIsAnon === bIsAnon) {
                    const aName = a.evaluator?.profile ? `${a.evaluator.profile.familyName} ${a.evaluator.profile.givenName}`.toLowerCase() : '';
                    const bName = b.evaluator?.profile ? `${b.evaluator.profile.familyName} ${b.evaluator.profile.givenName}`.toLowerCase() : '';
                    return sortDirection === 'ASC' ? aName.localeCompare(bName) : bName.localeCompare(aName);
                }

                if (aIsAnon) return sortDirection === 'ASC' ? 1 : -1;
                if (bIsAnon) return sortDirection === 'ASC' ? -1 : 1;
                return 0;
            });
        }

        return result;
    }, [searchTerm, tableInfo, sortBy, sortDirection]);

    const canClickOnEvaluation = (info: Evaluation) => {
        return info.status === EvaluationStatus.SUBMITTED || (!isOwnerAdminHr && info.status === EvaluationStatus.UPDATE_REQUESTED);
    };

    const handleSort = (newSortBy: EvaluationSortBy) => {
        setSortDirection(prev => (newSortBy === sortBy ? (prev === 'ASC' ? 'DESC' : 'ASC') : 'DESC'));
        setSortBy(newSortBy);
    };

    const getArrowClass = (column: EvaluationSortBy) => {
        if (sortBy !== column) return 'invoices__header-cell--sortable__arrow';
        return `invoices__header-cell--sortable__arrow invoices__header-cell--sortable__arrow--${sortDirection.toLowerCase()}`;
    };

    return (
        <div style={{ marginTop: '30px', width: '100%' }} className="dashboard-leave-requests ">
            <Table className="invoices__table">
                <TableRow className="invoices__header-cell--background">
                    <TableHeadCell className="invoices__header-cell--background">
                        <div className="invoices__header-cell">
                            <div className="invoices__header-cell--sortable">
                                <span>{t('evaluator')}</span>
                                <img
                                    src={sortBy === EvaluationSortBy.EVALUATOR ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                    alt="Arrow"
                                    className={getArrowClass(EvaluationSortBy.EVALUATOR)}
                                    onClick={() => handleSort(EvaluationSortBy.EVALUATOR)}
                                />
                            </div>
                        </div>
                    </TableHeadCell>
                    <TableHeadCell className="invoices__header-cell--background ">
                        <div className="invoices__header-cell invoices__header-cell--sortable">
                            {t('evaluatee')}
                            <img
                                src={sortBy === EvaluationSortBy.EVALUATEE ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                alt="Arrow"
                                className={getArrowClass(EvaluationSortBy.EVALUATEE)}
                                onClick={() => handleSort(EvaluationSortBy.EVALUATEE)}
                            />
                        </div>
                    </TableHeadCell>
                    <TableHeadCell className="invoices__header-cell--background ">
                        <div className="invoices__header-cell invoices__header-cell--sortable">
                            {t('type')}
                            <img
                                src={sortBy === EvaluationSortBy.IS_ANONYMOUS ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                alt="Arrow"
                                className={getArrowClass(EvaluationSortBy.IS_ANONYMOUS)}
                                onClick={() => handleSort(EvaluationSortBy.IS_ANONYMOUS)}
                            />
                        </div>
                    </TableHeadCell>
                    {activeTab === 2 && (
                        <TableHeadCell className="invoices__header-cell--background ">
                            <div className="invoices__header-cell invoices__header-cell--sortable">
                                {t('reason')}
                                <img
                                    src={sortBy === EvaluationSortBy.UPDATE_REASON ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                    alt="Arrow"
                                    className={getArrowClass(EvaluationSortBy.UPDATE_REASON)}
                                    onClick={() => handleSort(EvaluationSortBy.UPDATE_REASON)}
                                />
                            </div>
                        </TableHeadCell>
                    )}
                    <TableHeadCell className="invoices__header-cell--background">
                        <div className="invoices__header-cell invoices__header-cell--sortable">
                            {t('dueDate')}
                            <img
                                src={sortBy === EvaluationSortBy.DUE_DATE ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                alt="Arrow"
                                className={getArrowClass(EvaluationSortBy.DUE_DATE)}
                                onClick={() => handleSort(EvaluationSortBy.DUE_DATE)}
                            />
                        </div>
                    </TableHeadCell>
                    <TableHeadCell className="invoices__header-cell--background">
                        <div className="invoices__header-cell invoices__header-cell--sortable">
                            {t('evaluationRoles')}
                            <img
                                src={sortBy === EvaluationSortBy.ROLE ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                alt="Arrow"
                                className={getArrowClass(EvaluationSortBy.ROLE)}
                                onClick={() => handleSort(EvaluationSortBy.ROLE)}
                            />
                        </div>
                    </TableHeadCell>
                    {activeTab === 0 && (
                        <TableHeadCell className="invoices__header-cell--background">
                            <div className="invoices__header-cell invoices__header-cell--sortable">
                                {t('status')}
                                <img
                                    src={
                                        sortBy === EvaluationSortBy.EXPIRATION_STATUS
                                            ? '/svg/common/sort-arrow-blue.svg'
                                            : '/svg/common/sort-arrow.svg'
                                    }
                                    alt="Arrow"
                                    className={getArrowClass(EvaluationSortBy.EXPIRATION_STATUS)}
                                    onClick={() => handleSort(EvaluationSortBy.EXPIRATION_STATUS)}
                                />
                            </div>
                        </TableHeadCell>
                    )}
                    <div className="invoices__header-cell--background"></div>
                </TableRow>

                {filteredTableInfo.length < 1 ? (
                    <TableRow>
                        <TableCell colSpan={activeTab === 1 ? 4 : 5} className="no-result__background">
                            <NoResultsFound img="/svg/no-results-found.svg" title={t('noResultsFound')} paragraph={t('noResultsFoundParagraph')} />
                        </TableCell>
                    </TableRow>
                ) : (
                    filteredTableInfo.map((info, index) => {
                        const isEvaluatorAndActive =
                            (buttonType === 'perform' || buttonType === 'edit') && isEvaluator(info.evaluator?.userSub) && !isExpired(info.dueDate);

                        const canApproveAndActive = buttonType === 'approve' && isOwnerAdminHr && !isExpired(info.dueDate);

                        const canShowPrimaryButton = isEvaluatorAndActive || canApproveAndActive;

                        const showUpdateRequestedTag = info.status === EvaluationStatus.UPDATE_REQUESTED && !isOwnerAdminHr;

                        const handlePrimaryAction = () => {
                            if (buttonType === 'perform') {
                                navigate(`/perform-evaluation/${info.id}`);
                                return;
                            }
                            if (buttonType === 'edit') {
                                setIsModalVisible?.(true);
                                setEvaluationId?.(info.id);
                                return;
                            }

                            handleApproveOrReject?.(info.id, EvaluationStatus.UPDATE_APPROVED);
                        };

                        const handleSecondaryAction = () => {
                            handleApproveOrReject?.(info.id, EvaluationStatus.SUBMITTED);
                        };

                        const iconUrlPrimary =
                            buttonType === 'perform'
                                ? '/svg/play-no-circle.svg'
                                : buttonType === 'approve'
                                ? '/svg/circle-check-green.svg'
                                : '/svg/pen-grey.svg';

                        return (
                            <TableRow
                                key={info.id}
                                className={`invoices__row invoices__cell ${index % 2 === 0 ? 'invoices__row--white' : 'invoices__row--gray'}`}
                            >
                                <TableCell className="invoices__cell--background">
                                    {info.evaluator?.profile
                                        ? info.evaluator?.profile.givenName + ' ' + info.evaluator?.profile.familyName
                                        : t('anonymous')}
                                </TableCell>
                                <TableCell className="invoices__cell--background">
                                    {info.evaluatee?.profile.givenName + ' ' + info.evaluatee?.profile.familyName}
                                </TableCell>
                                <TableCell className="invoices__cell--background">{info.isAnonymous ? t('anonymous') : t('public')}</TableCell>
                                {activeTab === 2 && (
                                    <TableCell className="invoices__cell--background">
                                        {info.updateReason ? info.updateReason : <span className="evaluations__no-reason">{t('noReason')}</span>}
                                    </TableCell>
                                )}

                                <TableCell className="invoices__cell--background">{formatToFullMonthDayYear(info.dueDate.toString())}</TableCell>
                                <TableCell className="invoices__cell--background">{displayRoles(info.evaluatorToEvaluateeRole.direction)}</TableCell>
                                {activeTab === 0 && (
                                    <TableCell className="invoices__cell--background">
                                        {isExpired(info.dueDate) ? (
                                            <StatusBadge status={t('expired')} className="status-badge--overdue" />
                                        ) : info.status === EvaluationStatus.UPDATE_APPROVED ? (
                                            <StatusBadge status={t('revisit')} className="status-badge--ready" />
                                        ) : (
                                            <StatusBadge status={t('active')} className="status-badge--paid" />
                                        )}
                                    </TableCell>
                                )}

                                <div className="evaluations__actions">
                                    {showUpdateRequestedTag ? (
                                        <p>update requested</p>
                                    ) : (
                                        canShowPrimaryButton && (
                                            <IconButton variant="icon" onClick={handlePrimaryAction} iconAlt={buttonType} iconUrl={iconUrlPrimary} />
                                        )
                                    )}

                                    {buttonType2 && !isExpired(info.dueDate) && (
                                        <IconButton
                                            variant="icon"
                                            onClick={handleSecondaryAction}
                                            iconAlt={buttonType2}
                                            iconUrl="/svg/circle-cancel.svg"
                                        />
                                    )}

                                    {canClickOnEvaluation(info) && (
                                        <IconButton
                                            variant="icon"
                                            onClick={() => navigate(`/evaluation-preview/${info.id}`)}
                                            iconAlt="preview"
                                            iconUrl="/svg/eye-grey.svg"
                                        />
                                    )}
                                </div>
                            </TableRow>
                        );
                    })
                )}
                {filteredTableInfo.length > 0 && isReady && (
                    <TableRow className="invoices__row">
                        <TableCell colSpan={6}>
                            <div className="invoices__pagination">
                                <Pagination
                                    total={pagination.total}
                                    elementsPerPage={pagination.perPage}
                                    setElementsPerPage={pagination.setPerPage}
                                    page={pagination.page}
                                    setPage={page => pagination.handlePageChange(page)}
                                />
                            </div>
                        </TableCell>
                    </TableRow>
                )}
            </Table>
        </div>
    );
}
